"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import { GalleryVerticalIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { CarouselIndicator } from "../Carouselndicator";
import { ArtGalleryModal } from "./ArtGalleryModal";

interface ArtGalleryCarouselProps {
  imageURLs: string[];
  gameTitle?: string;
  gameIcon?: string;
  className?: string;
  gameId?: string;
}

export const ArtGalleryCarousel = ({
  imageURLs,
  gameTitle,
  gameIcon,
  className,
  gameId,
}: ArtGalleryCarouselProps) => {
  const { track } = useGamePageV3Analytics();
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === imageURLs.length ? 1 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [imageURLs.length]);

  const handleOpenModal = useCallback(() => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-open",
          control: "ArtGallery",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
        },
      });
    }
    setIsModalOpen(true);
  }, [gameId, gameTitle, track]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") handleOpenModal();
    },
    [handleOpenModal],
  );

  return (
    <>
      <ArtGalleryModal
        imageURLs={imageURLs}
        gameTitle={gameTitle}
        gameIcon={gameIcon}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        gameId={gameId}
      />

      <div
        className={cn(
          "relative glass-container-border cursor-pointer",
          className,
        )}
        onClick={handleOpenModal}
        onKeyDown={handleKeyDown}
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
      >
        {/* Grid layout to stack images on top of each other */}
        <div className="absolute inset-0 grid grid-cols-1 grid-rows-1">
          {imageURLs.map((url, index) => (
            <Image
              key={url}
              src={url}
              priority={index === currentIndex - 1}
              alt={`Art Gallery ${index + 1}`}
              fill
              loading={index === currentIndex - 1 ? "eager" : "lazy"}
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              className={cn(
                "col-start-1 row-start-1 object-cover rounded-[calc(1.25rem-1px)] transition-all duration-200",
                index === currentIndex - 1 ? "opacity-100" : "opacity-0",
              )}
            />
          ))}
        </div>

        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        <div className="absolute left-[10%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <GalleryVerticalIcon className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Art Gallery</p>
        </div>

        <CarouselIndicator
          currentIndex={currentIndex}
          total={imageURLs.length}
          className="absolute bottom-5 right-5"
        />
      </div>
    </>
  );
};
